import { useEffect, useRef, useCallback } from 'react';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Loader2 } from 'lucide-react';
import { useChatStore } from '@/stores/chat-store';
import { MessageBubble } from './message-bubble';

export function MessageList() {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    isLoadingMessages,
    hasMoreMessages,
    selectedConversation,
    fetchMessages
  } = useChatStore();

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  // Load more messages when scrolling to top
  const handleScroll = useCallback(() => {
    if (!containerRef.current || isLoadingMessages || !hasMoreMessages) return;

    const { scrollTop } = containerRef.current;
    if (scrollTop < 100 && selectedConversation) {
      fetchMessages(selectedConversation.id, true);
    }
  }, [isLoadingMessages, hasMoreMessages, selectedConversation, fetchMessages]);

  // Format date separator
  const formatDateSeparator = (date: Date) => {
    if (isToday(date)) return 'Hoje';
    if (isYesterday(date)) return 'Ontem';
    return format(date, "d 'de' MMMM", { locale: ptBR });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups: any[], message) => {
    const messageDate = new Date(message.createdAt);
    const lastGroup = groups[groups.length - 1];

    if (!lastGroup || !isSameDay(new Date(lastGroup.date), messageDate)) {
      groups.push({
        date: messageDate,
        messages: [message]
      });
    } else {
      lastGroup.messages.push(message);
    }

    return groups;
  }, []);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto p-4 space-y-4"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A227' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }}
    >
      {/* Loading more indicator */}
      {isLoadingMessages && hasMoreMessages && (
        <div className="flex justify-center py-2">
          <Loader2 className="w-5 h-5 text-brand-gold animate-spin" />
        </div>
      )}

      {/* Load more trigger */}
      {hasMoreMessages && (
        <div ref={loadMoreTriggerRef} className="h-1" />
      )}

      {/* Empty state */}
      {messages.length === 0 && !isLoadingMessages && (
        <div className="flex items-center justify-center h-full">
          <p className="text-brand-ivory/50 text-sm">
            Nenhuma mensagem ainda
          </p>
        </div>
      )}

      {/* Messages grouped by date */}
      {groupedMessages.map((group, groupIndex) => (
        <div key={groupIndex} className="space-y-2">
          {/* Date separator */}
          <div className="flex items-center justify-center">
            <span className="px-3 py-1 bg-brand-smoke/80 rounded-full text-xs text-brand-ivory/60">
              {formatDateSeparator(group.date)}
            </span>
          </div>

          {/* Messages */}
          {group.messages.map((message: any, index: number) => {
            const prevMessage = index > 0 ? group.messages[index - 1] : null;
            const showAvatar = !prevMessage || prevMessage.direction !== message.direction;

            return (
              <MessageBubble
                key={message.id}
                message={message}
                showAvatar={showAvatar}
              />
            );
          })}
        </div>
      ))}

      {/* Loading indicator */}
      {isLoadingMessages && messages.length === 0 && (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 text-brand-gold animate-spin" />
        </div>
      )}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />
    </div>
  );
}
